import { daysOfTheWeekP, generalHelpers, millisecondsOneDay, millisecondsThreeHours, months } from '@monorepo/helpers';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { generalBlue, googleMapConfig } from 'src/app-config';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { HTHMarkerBuilding, HTHPolygonInfoBox } from 'src/components/_commons/house-to-house';
import { Loading, RadioSelector } from 'src/components/_commons';
import { typeHTHBuilding, typeHTHTerritory, typeHTHFace } from '@monorepo/models';
import { useGetConfig, useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    hthTerritories: typeHTHTerritory[];
};

type typeOption = '1' | '2' | '3';

export const HTHBuildingsActivity: FC<propsType> = ({ hthTerritories }) => {
    const [date1, setDate1] = useState<number | null>(null);
    const [date2, setDate2] = useState<number | null>(null);
    const [markerToShow, setMarkerToShow] = useState<typeHTHFace | null>(null);
    const [month, setMonth] = useState<number>(new Date().getMonth());
    const [radioValue, setRadioValue] = useState<typeOption>('1');
    const [weekDay, setWeekDay] = useState<number>(1);
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const centerCoords = hthTerritories[0].centerCoords;
    const config = useGetConfig();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();
    const mapLoader = useJsApiLoader(googleMapConfig);

    const buildings: typeHTHBuilding[] = useMemo(() => {
        let hs: typeHTHBuilding[] = [];
        if (radioValue === '1' && !date1) {
            return hs;
        }
        if (radioValue === '2' && (!date1 || !date2)) {
            return hs;
        }
        hthTerritories.forEach(t => {
            t.faces.forEach(p => {
                p.buildings?.forEach(b => {
                    if (!!b.coords) {
                        let filteredHouseholds: typeof b.households = [];
                        if (radioValue === '1' && date1) {
                            filteredHouseholds = b.households.filter(h =>
                                h.history.some(x =>
                                    !!x.state
                                    && x.timestamp > date1
                                    && x.timestamp < date1 + millisecondsOneDay
                                )
                            );
                        } else if (radioValue === '2' && date1) {
                            filteredHouseholds = b.households.filter(h =>
                                h.history.some(x =>
                                    !!x.state
                                    && x.timestamp > date1
                                    && x.timestamp < (date2 ?? 1)
                                )
                            );
                        } else {
                            filteredHouseholds = b.households.filter(h =>
                                h.history.some(x =>
                                    !!x.state
                                    && new Date(x.timestamp).getDay() === weekDay
                                    && new Date(x.timestamp).getMonth() === month
                                    && new Date(x.timestamp).getFullYear() === year
                                )
                            );
                        }
                        if (filteredHouseholds.length > 0) {
                            hs.push({
                                ...b,
                                households: filteredHouseholds,
                                street: p.street,
                                territoryNumber: t.territoryNumber,
                            });
                        }
                    }
                });
            });
        });
        hs.sort((a, b) => parseInt(a.territoryNumber!) - parseInt(b.territoryNumber!) || a.street!.localeCompare(b.street!) || a.streetNumber - b.streetNumber);
        return hs;
    }, [date1, date2, hthTerritories, month, radioValue, weekDay, year]);

    useEffect(() => {
        if (radioValue === '1') {
            setDate2(null);
        } else {
            if (date1) {
                const adjustedDate = new Date(date1);
                adjustedDate.setHours(23, 59, 59, 999);
                setDate2(adjustedDate.getTime());
            }
        }
    }, [date1, radioValue]);

    return (
        <div className={'mt-5'}>
            <style>{'.gm-style-iw-chr {height: 14px;}'}</style>
            <RadioSelector
                options={[
                    { label: radioValue === '1' ? 'Viendo por Fecha' : 'Ver por Fecha', type: '1' },
                    { label: radioValue === '2' ? 'Viendo por Rango' : 'Ver por Rango', type: '2' },
                    { label: radioValue === '3' ? 'Viendo por Día de S.' : 'Ver por Día de S.', type: '3' }
                ]}
                radioValue={radioValue}
                setRadioValue={setRadioValue}
            />
            <div className={'d-flex justify-content-center gap-2 mb-5'}>
                {['1', '2'].includes(radioValue) ?
                    <>
                    <div>
                        <input type={'date'}
                            className={'form-control'}
                            value={date1 ? generalHelpers.FormatDate(new Date(date1 - millisecondsThreeHours)) : ''}
                            onChange={e => {
                                const selectedDate = e.target.value ? new Date(e.target.value).getTime() + millisecondsThreeHours: null;
                                setDate1(selectedDate);
                            }}
                        />
                    </div>
                    {radioValue === '2' && date1 &&
                        <div>
                            <input type={'date'}
                                className={'form-control'}
                                value={date2 ? new Date(date2 - millisecondsThreeHours).toISOString().split('T')[0] : ''}
                                onChange={e => {
                                    const selectedDate = e.target.value ? new Date(e.target.value).getTime() + millisecondsThreeHours : null;
                                    if (!selectedDate) {
                                        return;
                                    }
                                    if (selectedDate < date1) {
                                        setDate2(date1);
                                    } else {
                                        const adjustedDate = new Date(selectedDate);
                                        adjustedDate.setHours(23, 59, 59, 999);
                                        setDate2(adjustedDate.getTime());
                                    }
                                }}
                            />
                        </div>
                    }
                    </>
                    :
                    <>
                    <div>
                        <select className={'form-select'}
                            value={weekDay}
                            onChange={e => setWeekDay(parseInt(e.target.value))}
                        >
                            {[...daysOfTheWeekP.slice(1), daysOfTheWeekP[0]].map((d, i) =>
                                <option key={d} value={i === 6 ? 0 : i + 1}>
                                    {d}
                                </option>
                            )}
                        </select>
                    </div>
                    <div className={'col-md-2'}>
                        <select className={'form-select'}
                            value={month}
                            onChange={e => setMonth(parseInt(e.target.value))}
                        >
                            {months.map((m, i) =>
                                <option key={m} value={i}>{m}</option>
                            )}
                        </select>
                    </div>
                    <div className={'col-md-2'}>
                        <select className={'form-select'}
                            value={year}
                            onChange={e => setYear(parseInt(e.target.value))}
                        >
                            {[2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033].map(y =>
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            )}
                        </select>
                    </div>
                    </>
                }
            </div>
            {((radioValue === '1' && date1) || (radioValue === '2' && date1 && date2) || radioValue === '3') &&
                <>
                {mapLoader.isLoaded ?
                    <GoogleMap
                        center={centerCoords}
                        id={googleMapConfig.id}
                        mapContainerClassName={'d-block m-auto'}
                        mapContainerStyle={{
                            height: isMobile ? '600px' : '500px',
                            width: '95%'
                        }}
                        zoom={17.8}
                    >
                        {buildings.map((b, i) =>
                            <HTHMarkerBuilding key={i}
                                building={b}
                                label={`T${b.territoryNumber} ${b.street} ${b.streetNumber}`}
                            />
                        )}
                        {hthTerritories.map(t =>
                            <div key={t.territoryNumber}>
                                {t.faces.map(currentFace =>
                                    <Fragment key={currentFace.id}>
                                        <Polygon
                                            editable={false}
                                            draggable={false}
                                            path={[currentFace.coordsPoint1, currentFace.coordsPoint2, currentFace.coordsPoint3]}
                                            onClick={() => setMarkerToShow(currentFace)}
                                            options={{
                                                clickable: true,
                                                fillColor: generalBlue,
                                                fillOpacity: 0.9,
                                                strokeColor: '',
                                                strokeOpacity: 0.8,
                                                strokePosition: google.maps.StrokePosition.INSIDE,
                                                strokeWeight: 7
                                            }}
                                        />
                                        {markerToShow?.id === currentFace.id &&
                                            <HTHPolygonInfoBox
                                                polygon={currentFace}
                                                territoryNumber={t.number}
                                                usingLettersForBlocks={config.isUsingLettersForBlocks}
                                            />
                                        }
                                    </Fragment>
                                )}
                            </div>
                        )}
                    </GoogleMap>
                    :
                    <Loading />
                }
                <div className={`${isDarkMode ? 'text-white' : ''} mt-5`}>
                    <h2>Predicación:</h2>
                    {!buildings.length &&
                        <div>
                            No se tocaron timbres
                        </div>
                    }
                    {buildings.map(b => (
                        <div key={`${b.street} ${b.streetNumber}`}>
                            {`T${b.territoryNumber} ${b.street} ${b.streetNumber}: se tocaron ${b.households.length} timbres`}
                        </div>
                    ))}
                </div>
                </>
            }
        </div>
    );
}
